import React, { useState, useEffect } from 'react';
import { DollarSign, Wallet, Gauge, TrendingUp, TrendingDown, Clock, Eye, EyeOff, HelpCircle } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import ErrorDisplay from './ErrorDisplay';
import { formatDateTimeET } from '../utils/datetime';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';
import { usePortfolioQuery } from '../store/tradingApiSlice';

interface AccountSummaryProps {
  refreshInterval?: number;
}

const AccountSummary: React.FC<AccountSummaryProps> = ({ refreshInterval = 30000 }) => {
  const {
    data: portfolio = null,
    isLoading: loading,
    isFetching: refreshing,
    error: portfolioError,
    refetch,
  } = usePortfolioQuery(undefined, {
    pollingInterval: refreshInterval,
    skipPollingIfUnfocused: true,
  });
  const blockingError = portfolioError && !portfolio ? 'Failed to fetch account data' : null;
  const refreshError = portfolioError && portfolio ? 'Live refresh failed; keeping the last account values.' : null;
  const [hideValues, setHideValues] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const saved = window.localStorage.getItem('accountSummaryHideValues');
    return saved === 'true';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('accountSummaryHideValues', hideValues ? 'true' : 'false');
    }
  }, [hideValues]);

  const getCurrencyIcon = (size = 14) => {
    const currency = portfolio?.data?.currency || 'USD';
    const symbol = getCurrencySymbol(currency);
    if (symbol !== '$') return <span className="font-bold text-[14px] text-muted">{symbol}</span>;
    return <DollarSign size={size} className="text-muted" />;
  };

  const isStale = !!portfolio?.data?.stale;
  const hasSnapshot = portfolio?.data?.snapshot_available !== false;
  const isInitializing = portfolio?.data?.connection_status === 'initializing';
  const isUnavailable = portfolio?.data?.connection_status === 'unavailable';
  const availableMarginPct = portfolio?.data?.available_margin_pct;

  useEffect(() => {
    if (!isInitializing) return;
    const retryId = window.setTimeout(() => refetch(), 2000);
    return () => window.clearTimeout(retryId);
  }, [isInitializing, portfolio?.timestamp, refetch]);

  return (
    <div className={`panel full-bleed-panel lg:h-full transition-all duration-300 ${isStale ? 'border-warning-500/20 bg-warning-500/5' : ''}`}>
      <div className="px-6 py-5 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg transition-colors ${isStale ? 'bg-warning-500/10 text-warning-500' : 'bg-neon/10 text-neon'}`}>
            <Wallet className="w-5 h-5" />
          </div>
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-white tracking-tight">
                Account Summary
              </h2>
              {isStale && (
                <div className="relative group cursor-help z-50">
                  <span className="text-[9px] uppercase font-black tracking-wider px-2 py-0.5 rounded-sm bg-warning-500/20 text-warning-500 border border-warning-500/30 animate-pulse">
                    {isInitializing ? 'Connecting' : hasSnapshot ? 'Stale Data' : 'Offline'}
                  </span>
                  <div className="absolute left-0 top-full mt-2 hidden group-hover:block w-64 p-3 bg-surface border border-warning-500/30 rounded-xl shadow-xl text-xs font-normal text-warning-500 leading-normal normal-case pointer-events-none">
                    <div className="absolute bottom-full left-4 border-4 border-transparent border-b-surface" />
                    <p className="font-bold mb-1">{isInitializing ? 'Synchronizing Account' : isUnavailable ? 'Account Data Unavailable' : hasSnapshot ? 'Account Data Stale' : 'No Cached Account Data'}</p>
                    <p className="opacity-80">
                      {isInitializing
                        ? 'IB is connected and account data is still synchronizing. This panel will refresh automatically.'
                        : isUnavailable
                        ? 'The IB socket is online, but account values did not arrive. The backend will keep retrying automatically.'
                        : hasSnapshot
                        ? 'Live streaming is currently unavailable. Displaying the last cached values recorded when connection was active.'
                        : 'The gateway is offline and this account does not have a persisted summary yet.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-muted">
              {isInitializing
                ? 'Connecting to IB and synchronizing account data'
                : isUnavailable
                ? 'IB connected — account values currently unavailable'
                : isStale
                ? hasSnapshot ? 'Showing offline cached snapshot' : 'Offline — no cached snapshot available'
                : 'Real-time portfolio and P&L oversight'}
            </p>
          </div>
        </div>
        <button
          onClick={() => setHideValues((prev) => !prev)}
          className="text-xs px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-foreground hover:bg-white/10 transition flex items-center gap-2"
          aria-label={hideValues ? 'Show account values' : 'Hide account values'}
        >
          {hideValues ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>

      <div className="p-4 space-y-5">
        {loading && !portfolio && !blockingError && (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {blockingError && (
          <ErrorDisplay
            error={blockingError}
            onRetry={() => refetch()}
            message="Failed to load account data"
          />
        )}

        {refreshError && (
          <div className="rounded-xl border border-warning-500/20 bg-warning-500/5 px-4 py-3 text-xs text-warning-300">
            {refreshError}
          </div>
        )}

        {portfolio?.success && !blockingError && isStale && !hasSnapshot && (
          <div className="rounded-xl border border-warning-500/20 bg-warning-500/5 px-4 py-8 text-center text-sm text-warning-300/80">
            {isInitializing
              ? 'Connecting to IB and synchronizing this account…'
              : isUnavailable
              ? 'IB is connected, but account values are unavailable. Synchronization will continue in the background.'
              : 'Connect this account once to create its first persisted account snapshot.'}
          </div>
        )}

        {portfolio?.success && !blockingError && (!isStale || hasSnapshot) && (
          <div className="space-y-4 text-sm text-foreground">


            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className={`rounded-xl transition p-3 space-y-2 ${
                isStale 
                  ? 'bg-warning-500/5 border border-warning-500/20 hover:border-warning-500/35' 
                  : 'bg-white/6 border border-white/10 hover:border-neon/30'
              }`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-muted">
                    Net liquidation
                  </p>
                  {getCurrencyIcon()}
                </div>
                <p
                  className={`mt-2 text-lg font-semibold text-foreground ${refreshing ? 'refreshing-text' : ''
                    }`}
                >
                  {formatCurrency(portfolio.data?.net_liquidation, portfolio.data?.currency, hideValues)}
                </p>
              </div>

              <div className={`rounded-xl transition p-3 space-y-2 ${
                isStale 
                  ? 'bg-warning-500/5 border border-warning-500/20 hover:border-warning-500/35' 
                  : 'bg-white/6 border border-white/10 hover:border-neon/30'
              }`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-muted">
                    Available funds
                  </p>
                  <Wallet size={14} className="text-muted" />
                </div>
                <p
                  className={`mt-2 text-lg font-semibold text-foreground ${refreshing ? 'refreshing-text' : ''
                    }`}
                >
                  {formatCurrency(portfolio.data?.available_funds, portfolio.data?.currency, hideValues)}
                </p>
                <div className="space-y-0.5 text-[11px]">
                  <div className="flex items-center justify-between gap-2 text-emerald-300">
                    <span
                      className="inline-flex items-center gap-1"
                      title="Percentage of net liquidation currently available for new positions."
                    >
                      Available margin
                      <HelpCircle className="h-3 w-3 text-emerald-300/70" aria-label="Available margin information" />
                    </span>
                    <span className="font-semibold">
                      {availableMarginPct !== undefined && availableMarginPct !== null
                        ? `${availableMarginPct.toFixed(2)}%`
                        : '—'}
                    </span>
                  </div>
                  {portfolio.data?.init_margin_req !== undefined && portfolio.data?.init_margin_req !== null && (
                    <div className="flex items-center justify-between gap-2 text-muted">
                      <span
                        className="inline-flex items-center gap-1"
                        title="Initial margin currently reserved by open positions."
                      >
                        Initial margin used
                        <HelpCircle className="h-3 w-3 text-muted/70" aria-label="Initial margin information" />
                      </span>
                      <span>{formatCurrency(portfolio.data.init_margin_req, portfolio.data.currency, hideValues)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className={`rounded-xl transition p-3 space-y-2 ${
                isStale 
                  ? 'bg-warning-500/5 border border-warning-500/20 hover:border-warning-500/35' 
                  : 'bg-white/6 border border-white/10 hover:border-neon/30'
              }`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-muted">
                    Buying power
                  </p>
                  <Gauge size={14} className="text-muted" />
                </div>
                <p
                  className={`mt-2 text-lg font-semibold text-foreground ${refreshing ? 'refreshing-text' : ''
                    }`}
                >
                  {formatCurrency(portfolio.data?.buying_power, portfolio.data?.currency, hideValues)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className={`rounded-xl transition p-3 space-y-2 ${
                isStale 
                  ? 'bg-warning-500/5 border border-warning-500/20 hover:border-warning-500/35' 
                  : 'bg-white/6 border border-white/10 hover:border-neon/30'
              }`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-muted">
                    Realized P&amp;L (today)
                  </p>
                  {portfolio.data?.realized_pnl_today !== undefined &&
                    portfolio.data?.realized_pnl_today !== null &&
                    portfolio.data?.realized_pnl_today < 0 ? (
                    <TrendingDown size={14} className="text-red-400" />
                  ) : (
                    <TrendingUp size={14} className="text-emerald-400" />
                  )}
                </div>
                <p
                  className={`mt-2 text-lg font-semibold ${refreshing ? 'refreshing-text' : 'text-foreground'
                    }`}
                >
                  {formatCurrency(portfolio.data?.realized_pnl_today, portfolio.data?.currency, hideValues)}
                </p>
              </div>

              <div className={`rounded-xl transition p-3 space-y-2 ${
                isStale 
                  ? 'bg-warning-500/5 border border-warning-500/20 hover:border-warning-500/35' 
                  : 'bg-white/6 border border-white/10 hover:border-neon/30'
              }`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-muted">
                    Unrealized P&amp;L (today)
                  </p>
                  {portfolio.data?.unrealized_pnl_today !== undefined &&
                    portfolio.data?.unrealized_pnl_today !== null &&
                    portfolio.data?.unrealized_pnl_today < 0 ? (
                    <TrendingDown size={14} className="text-red-400" />
                  ) : (
                    <TrendingUp size={14} className="text-emerald-400" />
                  )}
                </div>
                <p
                  className={`mt-2 text-lg font-semibold ${refreshing ? 'refreshing-text' : 'text-foreground'
                    }`}
                >
                  {formatCurrency(portfolio.data?.unrealized_pnl_today, portfolio.data?.currency, hideValues)}
                </p>
              </div>

              {portfolio.data?.pnl_timestamp && (
                <div className={`rounded-xl transition p-3 space-y-2 ${
                  isStale 
                    ? 'bg-warning-500/5 border border-warning-500/20' 
                    : 'bg-white/6 border border-white/10'
                }`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-muted">
                      P&amp;L timestamp
                    </p>
                    <Clock size={14} className="text-muted" />
                  </div>
                  <p className="mt-2 text-xs font-semibold text-foreground">
                    {formatDateTimeET(portfolio.data?.pnl_timestamp)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountSummary;
